import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-brand-primary text-white shadow-xs hover:bg-brand-primary-70 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-slate-300",
        destructive:
          "bg-error-light text-error-foreground shadow-2xs hover:bg-error-light/90 border border-transparent hover:border hover:border-error-foreground transition",
        // Destructive secondary (cheatsheet §5, decisión Santi 2026-05-04):
        // acción destructiva-pero-reversible (Desactivar, Anular, Cancelar
        // recordatorio). En reposo se ve sutil — border neutro, texto normal —
        // y al hover muestra la intención destructiva con texto rojo + bg
        // error-light suave. Sirve para vivir en barras de acción junto a
        // un `destructive` (full red bg) sin competir visualmente.
        destructiveOutline:
          "bg-transparent border border-border text-text-primary hover:bg-error-light/40 hover:text-error-foreground hover:border-error-border transition",
        outline:
          "border border-brand-primary bg-transparent shadow-2xs hover:bg-brand-primary hover:text-white dark:border-slate-400 dark:text-slate-200 dark:hover:bg-slate-200 dark:hover:text-slate-900 dark:hover:border-slate-200",
        secondary:
          "bg-secondary text-secondary-foreground shadow-2xs hover:bg-secondary/80 border border-transparent hover:border hover:border-border transition",
        ghost: "text-text-primary hover:bg-background-400 hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
        success:
          "bg-success-light text-success-foreground shadow-2xs hover:bg-success-light border border-transparent hover:border hover:border-success-foreground transition",
        info: "bg-info-light text-info-foreground shadow-2xs hover:bg-info-hover",
        warning:
          "bg-warning-light text-warning-foreground shadow-2xs hover:bg-warning-hover",
        muted:
          "bg-transparent border border-border text-text-primary hover:border-text-primary transition hover:bg-background-200",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type="button"
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button };
